import * as React from "react"
import { Button } from "@/components/ui/button"
import { csrfToken } from "@/lib/csrf"

/**
 * One member's phone number on the run history table, masked until somebody
 * asks for it.
 *
 * The table lists every member a run touched — five figures for a District
 * Council, ~93,000 union-wide — so printing the numbers made the screen a
 * membership contact export. The last four digits answer what it is actually
 * asked ("is this the number we texted?"); the whole number is one request
 * away, and the server records who asked.
 *
 * Raw fetch(), not Inertia's router: revealing one row must not navigate away
 * from a filtered, paginated page. AGENTS.md carves out exactly this case.
 */
export function RevealablePhone({
  deliveryId,
  masked,
  revealable,
}: {
  deliveryId: number
  masked: string | null
  revealable: boolean
}) {
  const [revealed, setRevealed] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  if (!revealable) return <span className="text-ink-muted">—</span>
  if (revealed) return <span>{revealed}</span>

  const reveal = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`/deliveries/${deliveryId}/reveal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      })
      const body = (await response.json()) as {
        phone_number?: string
        error?: string
      }
      if (body.phone_number) setRevealed(body.phone_number)
      else setError(body.error ?? "That number couldn't be shown.")
    } catch {
      setError("That number couldn't be shown.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <span className="flex items-center gap-2">
      <span>{masked ?? "—"}</span>
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={reveal}
        disabled={loading}
        aria-label="Show the full phone number"
      >
        {loading ? "Showing…" : "Show"}
      </Button>
      {error && <span className="text-xs text-danger-display">{error}</span>}
    </span>
  )
}
